import { useQuery } from "@apollo/client";
import round from "lodash/round";
import zipObject from "lodash/zipObject";
import { useMemo } from "react";

import { useGetFindingFiltersStore } from "../findings-table/hooks/use-finding-filters-store";
import { useFindingsQuery } from "../hooks";
import { GET_FINDING_STATS, GET_ORGANIZATION_EXPOSURE } from "../queries";
import type { FindingFiltersInput } from "gql/graphql";

interface IFindingHeaderIndicators {
  findingStatus: { safe: number; vulnerable: number };
  fixesAvailable: number;
  riskExposure: number;
  totalCvssf: number;
  totalFindings: number;
  vulnsStatus: { safe: number; vulnerable: number };
}

const useFindingHeaderIndicators = (
  groupName: string,
  organizationName: string,
): IFindingHeaderIndicators => {
  const filtersStore = useGetFindingFiltersStore(groupName);
  const { findings } = useFindingsQuery(
    groupName,
    filtersStore as unknown as FindingFiltersInput,
  );
  const { data: dataFindingStats } = useQuery(GET_FINDING_STATS, {
    variables: {
      filters: filtersStore as unknown as FindingFiltersInput,
      groupName,
    },
  });
  const { data: orgExposureData } = useQuery(GET_ORGANIZATION_EXPOSURE, {
    variables: { organizationId: organizationName },
  });

  const totalFindings = useMemo((): number => {
    const uniques = new Set(findings.map((finding): string => finding.title));

    return uniques.size;
  }, [findings]);
  const findingStatus = useMemo((): { safe: number; vulnerable: number } => {
    return findings.reduce(
      (
        acc: { safe: number; vulnerable: number },
        finding,
      ): { safe: number; vulnerable: number } => {
        return {
          ...acc,
          safe: finding.status === "SAFE" ? acc.safe + 1 : acc.safe,
          vulnerable:
            finding.status === "VULNERABLE"
              ? acc.vulnerable + 1
              : acc.vulnerable,
        };
      },
      { safe: 0, vulnerable: 0 },
    );
  }, [findings]);

  const vulnsStatus = useMemo((): { safe: number; vulnerable: number } => {
    return {
      safe: findings.reduce(
        (acc: number, finding): number => acc + finding.vulnStats.closed,
        0,
      ),
      vulnerable: findings.reduce(
        (acc: number, finding): number => acc + finding.vulnStats.open,
        0,
      ),
    };
  }, [findings]);
  const fixesAvailable = useMemo((): number => {
    return dataFindingStats?.group.findingStats.fixable ?? 0;
  }, [dataFindingStats]);

  const totalCvssf = useMemo((): number => {
    return round(dataFindingStats?.group.totalOpenCvssf ?? 0, 2);
  }, [dataFindingStats]);

  const organizationGroups = useMemo((): string[] => {
    const analytics = orgExposureData?.organization.analytics;
    if (analytics === null || analytics === undefined) return [];

    const groupAnalytics = analytics as {
      axis?: { x?: { categories?: string[] } };
    };
    if (!groupAnalytics.axis?.x?.categories) return [];

    return groupAnalytics.axis.x.categories.map((category): string =>
      category.toLowerCase(),
    );
  }, [orgExposureData?.organization.analytics]);

  const groupsCvssfData = useMemo((): number[] => {
    const analytics = orgExposureData?.organization.analytics;
    if (analytics === null || analytics === undefined) return [];

    const analyticsData = analytics as { originalValues?: number[] };
    if (!analyticsData.originalValues) return [];

    return analyticsData.originalValues;
  }, [orgExposureData?.organization.analytics]);

  const riskExposure = useMemo((): number => {
    const group = groupName.toLowerCase();
    const groupCvssf =
      group in zipObject(organizationGroups, groupsCvssfData)
        ? zipObject(organizationGroups, groupsCvssfData)[group]
        : 0;
    const organizationCvssf =
      groupsCvssfData.length === 1
        ? groupsCvssfData[0]
        : groupsCvssfData.reduce(
            (acc: number, value: number): number => acc + value,
            0,
          );

    return organizationCvssf > 0
      ? round((groupCvssf / organizationCvssf) * 100, 2)
      : 0;
  }, [organizationGroups, groupsCvssfData, groupName]);

  return {
    findingStatus,
    fixesAvailable,
    riskExposure,
    totalCvssf,
    totalFindings,
    vulnsStatus,
  };
};

export { useFindingHeaderIndicators };
